import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  LayoutGrid, Users, CreditCard, Tag, RefreshCw, LogOut, ShieldCheck,
  ArrowLeft, Trash2, Euro, Loader2, Check, X, Database, Pencil, Save,
  Clock, Plus, ChevronLeft, ChevronRight, Search, Settings, Crown,
} from "lucide-react";
import SettingsView from "@/pages/admin/SettingsView";
import UsersView from "@/pages/admin/UsersView";
import SubscriptionsView from "@/pages/admin/SubscriptionsView";
import FeaturedView from "@/pages/admin/FeaturedView";
import ImgCachePanel from "@/pages/admin/ImgCacheView";
import { SEGMENTS } from "@/lib/segments";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}

const NAV = [
  { key: "overview", label: "Visão Geral", icon: LayoutGrid },
  { key: "users", label: "Utilizadores", icon: Users },
  { key: "subscriptions", label: "Subscrições", icon: CreditCard },
  { key: "featured", label: "Destaques", icon: Crown },
  { key: "data", label: "Gestão de Dados", icon: Database },
  { key: "payments", label: "Pagamentos", icon: CreditCard },
  { key: "plans", label: "Planos", icon: Tag },
  { key: "scrape", label: "Recolha de Dados", icon: RefreshCw },
  { key: "settings", label: "Definições", icon: Settings },
];

const Kpi = ({ label, value, gold, testid }) => (
  <div data-testid={testid} className="bg-[#18181b] border border-[#27272a] p-5">
    <div className="label-tiny mb-3">{label}</div>
    <div className={`font-head text-3xl font-light ${gold ? "text-[#c8aa6e]" : "text-[#fafafa]"}`}>{value}</div>
  </div>
);

function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setO(data)); }, []);
  if (!o) return <div className="text-[#71717a] text-sm">A carregar…</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Kpi testid="admin-revenue" label="Receita total" value={`${o.revenue}€`} gold />
        <Kpi label="Pagamentos" value={o.paid_count} />
        <Kpi testid="admin-users" label="Utilizadores" value={o.total_users} />
        <Kpi label="Acessos ativos" value={o.active_users} gold />
        <Kpi label="Subscritores" value={o.paying_users} />
        <Kpi label="Em trial" value={o.trial_users} />
      </div>
      <div className="bg-[#18181b] border border-[#27272a]">
        <div className="px-5 py-3 border-b border-[#27272a] label-tiny">Receita por plano</div>
        <div className="divide-y divide-[#27272a]">
          {o.by_plan.length === 0 && <div className="px-5 py-8 text-center text-sm text-[#71717a]">Ainda sem pagamentos.</div>}
          {o.by_plan.map((b) => (
            <div key={b.plan} className="px-5 py-3 flex items-center justify-between">
              <span className="text-sm text-[#fafafa]">{b.plan}</span>
              <span className="text-sm text-[#a1a1aa]">{b.count} pagamentos</span>
              <span className="font-head text-lg text-[#c8aa6e]">{b.total}€</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Payments() {
  const [tx, setTx] = useState([]);
  useEffect(() => { api.get("/admin/transactions").then(({ data }) => setTx(data)); }, []);
  return (
    <div className="bg-[#18181b] border border-[#27272a] overflow-x-auto" data-testid="admin-payments">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="label-tiny">
            <th className="px-4 py-3">Data</th><th className="px-4 py-3">Cliente</th>
            <th className="px-4 py-3">Plano</th><th className="px-4 py-3">Valor</th>
            <th className="px-4 py-3">Estado</th>
          </tr>
        </thead>
        <tbody>
          {tx.map((t) => (
            <tr key={t.id} className="border-t border-[#27272a]">
              <td className="px-4 py-2 text-[#a1a1aa] whitespace-nowrap">{fmtDate(t.created_at)}</td>
              <td className="px-4 py-2 text-[#fafafa]">{t.user_email}</td>
              <td className="px-4 py-2 text-[#a1a1aa]">{t.plan_name}</td>
              <td className="px-4 py-2 text-[#c8aa6e] font-mono">{t.amount}€</td>
              <td className="px-4 py-2">
                <span className={`text-xs px-2 py-0.5 border ${t.payment_status === "paid" ? "border-[#10b981] text-[#10b981]" : "border-[#3f3f46] text-[#a1a1aa]"}`}>{t.payment_status}</span>
              </td>
            </tr>
          ))}
          {tx.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#71717a] text-sm">Sem transações.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Plans() {
  const [plans, setPlans] = useState([]);
  const load = () => api.get("/billing/plans").then(({ data }) => setPlans(data));
  useEffect(() => { load(); }, []);
  const save = async (p, amount) => {
    await api.put(`/admin/plans/${p.id}`, { amount: parseFloat(amount) });
    toast.success(`Plano ${p.name} atualizado`);
    load();
  };
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="admin-plans">
      {plans.map((p) => (
        <div key={p.id} className="bg-[#18181b] border border-[#27272a] p-5">
          <h3 className="font-head text-lg text-[#fafafa]">{p.name}</h3>
          <p className="text-xs text-[#71717a] mb-4">{p.interval}</p>
          <label className="label-tiny block mb-2">Preço (€)</label>
          <div className="flex items-center gap-2">
            <input id={`plan-${p.id}`} defaultValue={p.amount} type="number" step="1"
              className="w-28 bg-[#09090b] border border-[#3f3f46] text-[#fafafa] focus:border-[#c8aa6e] outline-none px-3 py-2 text-sm" />
            <button onClick={() => save(p, document.getElementById(`plan-${p.id}`).value)}
              className="bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-3 py-2 text-sm" data-testid={`save-plan-${p.id}`}>Guardar</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function EditProfileDialog({ profile, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Sync incoming profile to local form using the "adjust state during render"
  // pattern (avoids set-state-in-effect).
  const [prevId, setPrevId] = useState(null);
  const curId = profile?.id || null;
  if (curId !== prevId) {
    setPrevId(curId);
    setForm(profile ? {
      name: profile.name || "", city: profile.city || "", phone: profile.phone || "",
      segment: profile.segment || "", description: profile.description || "",
    } : {});
  }

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.segment) delete body.segment;
      await api.patch(`/admin/profiles/${profile.id}`, body);
      toast.success("Perfil atualizado");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!profile} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#18181b] border border-[#3f3f46] rounded-none text-[#fafafa] max-w-lg" data-testid="admin-edit-dialog">
        <DialogHeader><DialogTitle className="font-head font-normal text-lg">Editar perfil</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {[["name", "Nome"], ["city", "Cidade"], ["phone", "Telefone"]].map(([k, label]) => (
            <div key={k}>
              <label className="label-tiny block mb-1">{label}</label>
              <input value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`edit-${k}`}
                className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]" />
            </div>
          ))}
          <div>
            <label className="label-tiny block mb-1">Categoria</label>
            <select value={form.segment || ""} onChange={(e) => setForm((f) => ({ ...f, segment: e.target.value }))} data-testid="edit-segment"
              className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]">
              <option value="">— manter automática —</option>
              {SEGMENTS.map((sg) => <option key={sg.k} value={sg.k}>{sg.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny block mb-1">Descrição</label>
            <textarea rows={4} value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="edit-description"
              className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e] resize-none" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa]">Cancelar</button>
            <button onClick={save} disabled={saving} data-testid="edit-save" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#c8aa6e] text-black hover:bg-[#d4b982] disabled:opacity-60">
              <Save className="w-4 h-4" /> Guardar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DataManagement() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const pageSize = 20;

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/profiles", { params: { search, page, page_size: pageSize } })
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .finally(() => setLoading(false));
  }, [search, page]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const [reclassifying, setReclassifying] = useState(false);
  const reclassify = async () => {
    setReclassifying(true);
    try {
      const { data } = await api.post("/admin/profiles/reclassify");
      const c = data.counts || {};
      toast.success(`Reclassificado: ${c.mulheres || 0} mulheres · ${c.trans || 0} trans · ${c.homens || 0} homens · ${c.massagens || 0} massagens`);
      load();
    } catch (e) { toast.error("Erro ao reclassificar"); }
    finally { setReclassifying(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Apagar o perfil "${p.name}"? Esta ação é irreversível.`)) return;
    try { await api.delete(`/admin/profiles/${p.id}`); toast.success("Perfil apagado"); load(); }
    catch (e) { toast.error("Erro ao apagar"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-data">
      <div className="bg-[#18181b] border border-[#27272a]">
        <div className="p-4 border-b border-[#27272a]">
          <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="w-4 h-4 text-[#71717a] absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="admin-data-search"
              placeholder="Pesquisar nome, número ou cidade…"
              className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none pl-9 pr-3 py-2 text-sm" />
          </div>
          <button onClick={reclassify} disabled={reclassifying} data-testid="admin-reclassify"
            className="border border-[#27272a] text-[#fafafa] hover:border-[#c8aa6e] px-3 py-2 text-sm disabled:opacity-60 whitespace-nowrap">
            {reclassifying ? "A reclassificar…" : "Reclassificar categorias"}
          </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="label-tiny">
              <th className="px-4 py-3">Foto</th><th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Cidade</th><th className="px-4 py-3">Site</th>
              <th className="px-4 py-3">Categoria</th><th className="px-4 py-3">Telefone</th>
              <th className="px-4 py-3">Ações</th>
            </tr></thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={p.id} data-testid={`admin-data-row-${i}`} className="border-t border-[#27272a] hover:bg-[#27272a]/30">
                  <td className="px-4 py-2">
                    <img src={imgProxy(p.photo_url, 160)} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} alt="" loading="lazy" decoding="async" className="w-12 h-12 object-cover bg-[#27272a]" />
                  </td>
                  <td className="px-4 py-2 text-[#fafafa]">{p.name || "—"}</td>
                  <td className="px-4 py-2 text-[#a1a1aa]">{p.city || "—"}</td>
                  <td className="px-4 py-2 text-[#a1a1aa]">{p.source_name}</td>
                  <td className="px-4 py-2 text-[#a1a1aa]">{SEGMENTS.find((x) => x.k === p.segment)?.label || p.category}</td>
                  <td className="px-4 py-2 font-mono text-[#fafafa]">{p.phone || "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditing(p)} data-testid={`admin-edit-${i}`} className="text-[#a1a1aa] hover:text-[#c8aa6e]" title="Editar"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(p)} data-testid={`admin-delete-${i}`} className="text-[#71717a] hover:text-[#ef4444]" title="Apagar"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-[#71717a] text-sm">Nenhum perfil encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-[#27272a] flex items-center justify-between text-sm text-[#a1a1aa]">
          <span>{total} perfis</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 border border-[#3f3f46] disabled:opacity-30 hover:border-[#c8aa6e]" data-testid="admin-data-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-mono text-xs">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 border border-[#3f3f46] disabled:opacity-30 hover:border-[#c8aa6e]" data-testid="admin-data-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
      <EditProfileDialog profile={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function ScheduleConfig() {
  const [enabled, setEnabled] = useState(true);
  const [times, setTimes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [newTime, setNewTime] = useState("06:00");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/schedule").then(({ data }) => {
    setEnabled(data.enabled); setTimes(data.times || []); setJobs(data.jobs || []);
  });
  useEffect(() => { load(); }, []);

  const save = async (nextEnabled, nextTimes) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/schedule", { enabled: nextEnabled, times: nextTimes });
      setEnabled(data.enabled); setTimes(data.times || []); setJobs(data.jobs || []);
      toast.success("Horário atualizado");
    } catch (e) {
      toast.error("Erro ao guardar horário");
    } finally {
      setSaving(false);
    }
  };

  const addTime = () => {
    if (!/^\d{2}:\d{2}$/.test(newTime)) { toast.error("Formato HH:MM"); return; }
    if (times.includes(newTime)) return;
    save(enabled, [...times, newTime]);
  };
  const removeTime = (t) => save(enabled, times.filter((x) => x !== t));
  const toggleEnabled = () => save(!enabled, times);

  return (
    <div className="bg-[#18181b] border border-[#27272a] p-5" data-testid="schedule-config">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#c8aa6e]" /><h3 className="font-head text-base text-[#fafafa]">Horários da recolha automática</h3></div>
        <button onClick={toggleEnabled} disabled={saving} data-testid="schedule-toggle"
          className={`text-xs px-3 py-1.5 border ${enabled ? "border-[#10b981] text-[#10b981]" : "border-[#3f3f46] text-[#71717a]"}`}>
          {enabled ? "Ativo" : "Desativado"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {times.map((t) => (
          <span key={t} className="inline-flex items-center gap-2 bg-[#09090b] border border-[#3f3f46] px-3 py-1.5 text-sm text-[#fafafa] font-mono" data-testid={`schedule-time-${t}`}>
            {t} <button onClick={() => removeTime(t)} className="text-[#71717a] hover:text-[#ef4444]"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        {times.length === 0 && <span className="text-sm text-[#71717a]">Sem horários definidos.</span>}
      </div>
      <div className="flex items-center gap-2">
        <input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} data-testid="schedule-newtime"
          className="bg-[#09090b] border border-[#3f3f46] text-[#fafafa] px-3 py-2 text-sm outline-none focus:border-[#c8aa6e]" />
        <button onClick={addTime} disabled={saving} data-testid="schedule-add"
          className="inline-flex items-center gap-1.5 bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-3 py-2 text-sm font-medium disabled:opacity-60">
          <Plus className="w-4 h-4" /> Adicionar horário
        </button>
      </div>
      <p className="text-xs text-[#71717a] mt-3">Horas em UTC. {enabled ? `${jobs.length} execução(ões) agendada(s).` : "Recolha automática desativada."}</p>
    </div>
  );
}

function ScrapeView() {
  const [sources, setSources] = useState([]);
  const [last, setLast] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => {
    api.get("/sources").then(({ data }) => setSources(data));
    api.get("/scrape/status").then(({ data }) => { setLast(data.last_run); setRunning(data.running); });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(async () => {
      const { data } = await api.get("/scrape/status");
      setRunning(data.running); setLast(data.last_run);
      if (!data.running) { clearInterval(iv); load(); toast.success("Recolha concluída"); }
    }, 3000);
    return () => clearInterval(iv);
  }, [running]);
  const run = async () => {
    await api.post("/scrape");
    setRunning(true);
    toast.info("Recolha iniciada");
  };
  const runSource = async (key, name) => {
    try {
      await api.post(`/scrape/source/${key}`);
      setRunning(true);
      toast.info(`Recolha iniciada só para ${name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao iniciar recolha");
    }
  };
  const [seedBusy, setSeedBusy] = useState(false);
  const exportSeed = async () => {
    setSeedBusy(true);
    try {
      const { data } = await api.post("/admin/seed/export");
      toast.success(`Semente guardada: ${data.counts.profiles} perfis, ${data.counts.events} eventos (${(data.size_bytes / 1024 / 1024).toFixed(1)} MB)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar semente");
    } finally { setSeedBusy(false); }
  };
  const [commentsBusy, setCommentsBusy] = useState(false);
  const collectComments = async () => {
    setCommentsBusy(true);
    try {
      const { data } = await api.post("/admin/comments/collect", null, { params: { limit: 120 } });
      const totals = Object.values(data.summary || {}).reduce(
        (a, s) => ({ reviews: a.reviews + (s.reviews || 0), profiles: a.profiles + (s.with_reviews || 0) }),
        { reviews: 0, profiles: 0 });
      toast.success(`Comentários recolhidos: ${totals.reviews} de ${totals.profiles} perfis`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao recolher comentários");
    } finally { setCommentsBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="admin-scrape">
      <ScheduleConfig />
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={run} disabled={running}
          className="bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-4 py-2.5 font-medium text-sm flex items-center gap-2 disabled:opacity-60" data-testid="admin-run-scrape">
          <RefreshCw className={`w-4 h-4 ${running ? "spin" : ""}`} /> {running ? "A recolher…" : "Iniciar recolha agora"}
        </button>
        <button onClick={collectComments} disabled={commentsBusy || running}
          className="border border-[#27272a] text-[#fafafa] hover:border-[#c8aa6e] px-4 py-2.5 font-medium text-sm disabled:opacity-60" data-testid="admin-collect-comments">
          {commentsBusy ? "A recolher…" : "Recolher comentários dos sites"}
        </button>
        <button onClick={exportSeed} disabled={seedBusy || running}
          className="border border-[#27272a] text-[#fafafa] hover:border-[#c8aa6e] px-4 py-2.5 font-medium text-sm disabled:opacity-60" data-testid="admin-export-seed">
          {seedBusy ? "A guardar…" : "Guardar dados como semente (deploy)"}
        </button>
        <span className="text-xs text-[#71717a]">A semente é importada automaticamente no 1º arranque de um deploy com BD vazia.</span>
      </div>
      <ImgCachePanel />
      <div className="bg-[#18181b] border border-[#27272a] divide-y divide-[#27272a]">
        {sources.map((s) => (
          <div key={s.key} className="px-5 py-3 flex items-center gap-3" data-testid={`admin-source-${s.key}`}>
            <span className="w-1.5 h-8" style={{ background: s.color }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#fafafa]">{s.name}</div>
              <div className="text-xs text-[#71717a] break-words">{s.last_status}</div>
            </div>
            <div className="font-head text-lg text-[#c8aa6e] w-14 text-right">{s.count}</div>
            <button onClick={() => runSource(s.key, s.name)} disabled={running}
              data-testid={`admin-run-source-${s.key}`}
              className="border border-[#27272a] text-[#fafafa] hover:border-[#c8aa6e] px-3 py-1.5 text-xs disabled:opacity-60 whitespace-nowrap">
              Recolher só este site
            </button>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-[#71717a]">
        Mulheres VIP e Portuas estão protegidos por Cloudflare — requerem a chave ScraperAPI
        e o modo <span className="text-[#c8aa6e]">ultra_premium</span> (Definições → Anti-bot).
        Cada página em ultra_premium+render custa ~75 créditos, por isso use «Recolher só este site» para testar.
      </p>
    </div>
  );
}

export default function AdminPage() {
  const [view, setView] = useState("overview");
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex bg-[#09090b] min-h-screen">
      <aside className="w-60 shrink-0 border-r border-[#27272a] min-h-screen flex flex-col">
        <div className="px-6 py-6 border-b border-[#27272a]">
          <img src="/radarx-logo.png" alt="RadarX" className="h-9 w-auto" />
          <div className="label-tiny mt-2">Gestão SaaS</div>
        </div>
        <nav className="flex-1 py-4">
          {NAV.map((n) => {
            const Icon = n.icon; const active = view === n.key;
            return (
              <button key={n.key} data-testid={`admin-nav-${n.key}`} onClick={() => setView(n.key)}
                className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 ${active ? "border-[#c8aa6e] text-[#fafafa] bg-[#18181b]" : "border-transparent text-[#a1a1aa] hover:text-[#fafafa] hover:bg-[#18181b]/50"}`}>
                <Icon className="w-4 h-4" strokeWidth={1.5} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-[#27272a] p-4 space-y-2">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#a1a1aa] hover:text-[#fafafa]" data-testid="admin-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Painel de dados
          </button>
          <button onClick={logout} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#a1a1aa] hover:text-[#ef4444]">
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-10 bg-[#09090b]/90 backdrop-blur border-b border-[#27272a] px-6 lg:px-8 py-4">
          <h1 className="font-head text-xl font-normal text-[#fafafa]">
            {NAV.find((n) => n.key === view)?.label}
          </h1>
          <p className="text-xs text-[#71717a] mt-1">Sessão: {user?.email}</p>
        </header>
        <div className="p-6 lg:p-8 fade-up">
          {view === "overview" && <Overview />}
          {view === "users" && <UsersView />}
          {view === "subscriptions" && <SubscriptionsView />}
          {view === "featured" && <FeaturedView />}
          {view === "data" && <DataManagement />}
          {view === "payments" && <Payments />}
          {view === "plans" && <Plans />}
          {view === "scrape" && <ScrapeView />}
          {view === "settings" && <SettingsView />}
        </div>
      </main>
    </div>
  );
}
